import { FC, ReactNode } from 'react';

import {
  installAuthInterceptors,
  setAuthCallbacks,
  updateAuthTokens,
} from 'api/mutator/auth-refresh';
import useAuthenticationState from './useAuthentication';

/**
 * Wires the Orval axios instance to the React auth state, in one place.
 *
 * The auth-header + refresh-on-401 interceptors live as a module-level
 * singleton in 'api/mutator/auth-refresh'; this provider is the single owner
 * that installs them and keeps them fed with the current tokens and the
 * refresh/signout callbacks. Mount it exactly once, high in the tree (inside
 * RecoilRoot, above anything that makes API calls).
 *
 * We sync during render rather than in an effect on purpose: effects flush
 * bottom-up, so a child's effect/query could otherwise fire before this
 * provider's effect installed the interceptor or set the token. These calls
 * only assign module-level variables (no React state, no re-render) and are
 * idempotent, so running them on every render is safe and guarantees the axios
 * instance is ready before descendants render.
 */
const AuthAxiosProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { state, refreshTokens, signout } = useAuthenticationState();

  installAuthInterceptors();
  updateAuthTokens(state.token, state.refreshToken);
  setAuthCallbacks({
    onTokensRefreshed: refreshTokens,
    onRefreshFailed: signout,
  });

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

export default AuthAxiosProvider;
